import {
    Component, inject, Input, OnDestroy, OnInit
} from '@angular/core';
import OverlayModel from '../../../../models/overlay.model';
import {Store} from '@ngrx/store';
import VideoState from '../../../shared/stores/root-store/video.state';
import {
    selectActualVideoState,
    selectCurrentFrameFromState,
    selectCurrentTimeFromState,
    selectTotalTimeFromState
} from '../../../shared/stores/root-store/selectors/video.selector';
import {OverlayService} from '../../../shared/services/overlay.service';
import {NavigationType} from '../../../../enums/navigation.enum';
import {JumpToSceneAction, SetVideoStateTypeAction} from '../../../shared/stores/root-store/actions/video.action';
import {takeUntil, tap} from 'rxjs/operators';
import {combineLatest, Subject} from 'rxjs';
import {EventEnum} from '../../../../enums/event.enum';
import {EventTrackingService} from '../../../shared/services/event-tracking.service';
import {VideoStateEnum} from '../../../../enums/video-state.enum';
import {FrameService} from '../../../shared/services/frame.service';
import {BrowserService} from '../../../shared/services/browser.service';
import {BufferService} from '../../../shared/services/buffer.service';
import {PlayerEventApiEnum} from '../../../../enums/playerEventApi.enum';
import {EventEmitterService} from '../../../shared/services/event-emitter.service';

@Component({
    template: ''
})
export abstract class BaseComponent implements OnInit, OnDestroy {

    @Input() overlay: OverlayModel;
    isVisible = false;
    style;
    opacity = 1;
    position = {
        x: 0, y: 0
    };
    videoState: VideoStateEnum;
    isHoverState = false;

    protected store = inject<Store<{ video: VideoState }>>(Store);
    protected overlayService = inject(OverlayService);
    protected tracking = inject(EventTrackingService);
    protected bufferService = inject(BufferService);
    protected browseService = inject(BrowserService);
    protected frameService = inject(FrameService);
    protected eventEmitterService = inject(EventEmitterService);

    protected destroy$ = new Subject<void>();
    private showEventEmitted = false;
    private hideEventEmitted = true;

    ngOnInit() {
        combineLatest([
            this.store.select(selectCurrentTimeFromState),
            this.store.select(selectCurrentFrameFromState),
            this.store.select(selectActualVideoState),
            this.store.select(selectTotalTimeFromState)
        ]
        ).pipe(
            tap(([currentVideoTime, currentVideoFrame, videoState, totalTime]) => {
                this.overlayService.addTimeToLastSceneOverlay(this.overlay, totalTime);
                this.isVisible = this.overlayService.isDisplayed(currentVideoTime, this.overlay);
                this.opacity = this.overlayService.getCurrentOpacity(currentVideoFrame, this.overlay);
                this.videoState = videoState;
                if (this.isVisible) {
                    this.position = this.overlayService.getCurrentPosition(currentVideoFrame, this.overlay);
                }

                this.triggerEventsOnShowHideOverlay();

            }),
            takeUntil(this.destroy$)
        ).subscribe();

        this.style = this.overlayService.getStyle(this.overlay, this.position);
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    getStyle(isHover: boolean = false) {
        return this.overlayService.getStyle(this.overlay, this.position, isHover, this.isVisible);
    }

    toggleHoverState() {
        if (!this.browseService.isMobileDevice()) {
            this.isHoverState = !this.isHoverState;
        }
    }


    navigate() {
        const navigateTo = this.overlay.navigateTo;

        if (navigateTo.type === NavigationType.Video) {
            const frameToJump = this.frameService.getRoundFrame(+navigateTo.target) + 1.1;
            this.store.dispatch(JumpToSceneAction({
                navigationTime: this.frameService.getTimeFromFrame(frameToJump),
                videoState: this.videoState
            }));
            return;
        }

        if (navigateTo.type === NavigationType.Url) {
            window.open(this.getNavigationLink(navigateTo.target), '_blank');
            if (![VideoStateEnum.Ended, VideoStateEnum.Interaction].includes(this.videoState)) {
                this.store.dispatch(SetVideoStateTypeAction({
                    videoState: VideoStateEnum.UserPause
                }));
            }
        }
    }

    trackEvent() {
        const navigateTo = this.overlay.navigateTo;
        const reportingId = this.overlay.reportingId ? this.overlay.reportingId : '';

        this.tracking.track(EventEnum.ButtonClicked, {
            navigateTo,
            reportingId,
            buttonValue: 'clicked'
        });

        this.eventEmitterService.emitEvent(PlayerEventApiEnum.ButtonClicked, {
            navigateTo,
            reportingId,
            buttonValue: this.overlay.html
        });
    }

    /**
     * Hook to be implemented in subclass, if needed
     * @protected
     */
    protected onOverlayShow() {
        void 0;
    }

    /**
     * Hook to be implemented in subclass, if needed
     * @protected
     */
    protected onOverlayHide() {
        void 0;
    }

    private getNavigationLink(target) {
        return target.match(/^(https?|mailto|tel|sms):/) ? target : `//${target}`;
    }

    private triggerEventsOnShowHideOverlay() {
        if (this.isVisible && !this.showEventEmitted) {
            this.showEventEmitted = true;
            this.hideEventEmitted = false;

            this.eventEmitterService.emitEvent(PlayerEventApiEnum.OverlayShow, this.overlay);
            this.onOverlayShow();
        }

        if (!this.isVisible && !this.hideEventEmitted && this.showEventEmitted) {
            this.hideEventEmitted = true;
            this.showEventEmitted = false;

            this.eventEmitterService.emitEvent(PlayerEventApiEnum.OverlayHide, this.overlay);
            this.onOverlayHide();
        }
    }

    abstract onClick(event: MouseEvent): void;
}
